export type LengthOfStayParts = {
  years: string;
  months: string;
};

export type ResidenceParts = {
  barangay: string;
  cityMunicipality: string;
};

export type ParsedLengthOfStayDuration = {
  valid: boolean;
  years: number | null;
  months: number | null;
  totalMonths: number | null;
  message: string;
};

export type DerivedResidentTypeCode = "1" | "2" | "3";

export function parseLengthOfStay(value: string | null | undefined): LengthOfStayParts {
  const normalizedValue = normalizeCompositeValue(value);

  if (!normalizedValue || isNotApplicableValue(normalizedValue)) {
    return { years: "", months: "" };
  }

  return {
    years: /(\d+)\s*(?:YEARS?|YRS?|Y)\b/i.exec(normalizedValue)?.[1] ?? "",
    months: /(\d+)\s*(?:MONTHS?|MOS?|MO|M)\b/i.exec(normalizedValue)?.[1] ?? "",
  };
}

export function composeLengthOfStay(parts: LengthOfStayParts) {
  const years = normalizeNumericPart(parts.years);
  const months = normalizeNumericPart(parts.months);

  return [
    years ? `${years} years` : "",
    months ? `${months} months` : "",
  ].filter(Boolean).join(" ");
}

export function parseLengthOfStayDuration(
  value: string | null | undefined,
): ParsedLengthOfStayDuration {
  const normalizedValue = normalizeCompositeValue(value);

  if (!normalizedValue || isNotApplicableValue(normalizedValue)) {
    return {
      valid: true,
      years: null,
      months: null,
      totalMonths: null,
      message: "",
    };
  }

  if (/-\d+/.test(normalizedValue)) {
    return {
      valid: false,
      years: null,
      months: null,
      totalMonths: null,
      message: "Q34 Length of Stay must use non-negative years and months.",
    };
  }

  const parts = parseLengthOfStay(normalizedValue);
  const hasYears = parts.years !== "";
  const hasMonths = parts.months !== "";

  if (!hasYears && !hasMonths) {
    return {
      valid: false,
      years: null,
      months: null,
      totalMonths: null,
      message: "Q34 Length of Stay must include units, for example: 2 years 6 months.",
    };
  }

  const years = hasYears ? Number(parts.years) : 0;
  const months = hasMonths ? Number(parts.months) : 0;

  if (!Number.isSafeInteger(years) || !Number.isSafeInteger(months)) {
    return {
      valid: false,
      years: null,
      months: null,
      totalMonths: null,
      message: "Q34 Length of Stay must use valid numeric years and months.",
    };
  }

  if (months > 11) {
    return {
      valid: false,
      years,
      months,
      totalMonths: null,
      message: "Q34 Length of Stay months must be from 0 to 11.",
    };
  }

  return {
    valid: true,
    years,
    months,
    totalMonths: years * 12 + months,
    message: "",
  };
}

export function deriveResidentTypeCode(
  value: string | null | undefined,
): DerivedResidentTypeCode | null {
  const duration = parseLengthOfStayDuration(value);

  if (!duration.valid || duration.totalMonths === null) {
    return null;
  }

  if (duration.totalMonths >= 60) {
    return "1";
  }

  return duration.totalMonths > 6 ? "2" : "3";
}

export function isLengthOfStayAtLeastFiveYears(value: string | null | undefined) {
  const duration = parseLengthOfStayDuration(value);

  return duration.valid && duration.totalMonths !== null && duration.totalMonths >= 60;
}

export function shouldClearAutomatedResidenceValue(
  currentValue: string | null | undefined,
  generatedValue: string | null | undefined,
) {
  const current = normalizeCompositeValue(currentValue);
  const generated = normalizeCompositeValue(generatedValue);

  return Boolean(generated) && current === generated;
}

export function parseResidence(value: string | null | undefined): ResidenceParts {
  const normalizedValue = normalizeCompositeValue(value);

  if (!normalizedValue || isNotApplicableValue(normalizedValue)) {
    return { barangay: "", cityMunicipality: "" };
  }

  const separator = /\s*(?:,|\||\s\/\s)\s*/.exec(normalizedValue);

  if (!separator || separator.index === undefined) {
    return { barangay: normalizedValue, cityMunicipality: "" };
  }

  const separatorEnd = separator.index + separator[0].length;

  return {
    barangay: normalizedValue.slice(0, separator.index).trim(),
    cityMunicipality: normalizedValue.slice(separatorEnd).trim(),
  };
}

export function composeResidence(parts: ResidenceParts) {
  const barangay = normalizeCompositeValue(parts.barangay);
  const cityMunicipality = normalizeCompositeValue(parts.cityMunicipality);

  if (!cityMunicipality) {
    return barangay;
  }

  return `${barangay}, ${cityMunicipality}`;
}

export function normalizeNumericPart(value: string | null | undefined) {
  return String(value ?? "").replace(/\D+/g, "");
}

function normalizeCompositeValue(value: string | null | undefined) {
  return String(value ?? "").replace(/\s+/g, " ").trim();
}

function isNotApplicableValue(value: string) {
  const normalizedValue = value.toUpperCase();
  return normalizedValue.startsWith("99") || normalizedValue === "N/A" || normalizedValue === "NA" || normalizedValue === "NOT APPLICABLE";
}
